<template>
  <div class="blog-wrapper">
    <b-overlay
        :show="isLoading"
        rounded="sm"
    >
      <b-row class="blog-list-wrapper">
        <!-- Форма -->
        <b-card no-body class="col-md-12">
          <b-card-header class="pb-50">
            <h5>Импорт</h5>
          </b-card-header>
          <b-card-body>
            <b-row class="mb-1">
              <b-col cols="12">
                <b-form-radio-group
                    v-model="mode"
                    :options="modeOptions"
                    buttons
                    button-variant="outline-primary"
                    size="sm"
                />
              </b-col>
            </b-row>

            <b-row v-if="mode !== 'text'">
              <b-col :cols="hasQuery ? 5 : 10" class="mb-md-0 mb-2">
                <label class="text-nowrap mr-50">Ссылка:</label>
                <b-form-input
                    v-model="playlistLink"
                    placeholder="https://..."
                    @keyup.enter="submit"
                />
              </b-col>
              <b-col v-if="hasQuery" cols="5" class="mb-md-0 mb-2">
                <label class="text-nowrap mr-50">Запрос:</label>
                <b-form-input
                    v-model="searchQuery"
                    placeholder="Русский рок"
                    @keyup.enter="submit"
                />
              </b-col>
              <b-col cols="2" class="d-flex align-items-end mb-md-0 mb-2">
                <b-button
                    variant="primary"
                    :disabled="isLoading"
                    @click="submit"
                >
                  Найти
                </b-button>
              </b-col>
            </b-row>

            <b-row v-else>
              <b-col cols="10" class="mb-md-0 mb-2">
                <label class="text-nowrap mr-50">Список треков:</label>
                <b-form-textarea
                    v-model="tracklistText"
                    placeholder="Исполнитель - Песня (каждый трек на новой строке)"
                    rows="10"
                />
              </b-col>
              <b-col cols="2" class="d-flex align-items-end mb-md-0 mb-2">
                <b-button
                    variant="primary"
                    :disabled="isLoading"
                    @click="submit"
                >
                  Найти
                </b-button>
              </b-col>
            </b-row>

            <b-row v-if="errorMessage" class="mt-1">
              <b-col cols="12">
                <span class="text-danger">{{ errorMessage }}</span>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>

        <!-- Найденные плейлисты -->
        <b-card v-if="playlistsList.length > 0" no-body class="col-md-12">
          <b-table
              :items="playlistsList"
              responsive
              :fields="searchColumns"
              primary-key="id"
              show-empty
              empty-text="Ничего не найдено"
              class="position-relative"
          >
            <template #cell(playlist)="data">
              <b-media vertical-align="center">
                <template #aside>
                  <b-avatar
                      size="32"
                      :text="avatarText(data.item.title)"
                      variant="primary"
                  />
                </template>
                <span class="font-weight-bold d-block">{{ data.item.title }}</span>
              </b-media>
            </template>

            <template #cell(actions)="data">
              <div class="text-nowrap">
                <b-button
                    variant="primary"
                    class="btn-icon"
                    size="sm"
                    :disabled="isLoading"
                    @click="openPlaylist(data.item)"
                >
                  <feather-icon icon="ArrowRightCircleIcon"/>
                </b-button>
                <b-button
                    :href="playlistViewUrl(data.item)"
                    variant="primary"
                    class="btn-icon ml-1"
                    size="sm"
                    target="_blank"
                    title="Открыть в новой вкладке"
                >
                  <feather-icon icon="ExternalLinkIcon"/>
                </b-button>
                <b-button
                    v-if="data.item.url"
                    :href="data.item.url"
                    variant="warning"
                    class="btn-icon ml-1"
                    size="sm"
                    target="_blank"
                >
                  <feather-icon icon="CastIcon"/>
                </b-button>
              </div>
            </template>
          </b-table>

          <div class="my-2">
            <b-pagination
                v-if="total > 0"
                v-model="page"
                :per-page="perPage"
                align="center"
                :total-rows="total"
                first-number
                last-number
                prev-class="prev-item"
                next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18"/>
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18"/>
              </template>
            </b-pagination>
          </div>
        </b-card>

        <!-- Импортированные треки -->
        <b-card v-if="importResult" no-body class="col-md-12">
          <b-card-header class="pb-50">
            <h5 class="d-flex align-items-center mb-0">
              {{ importResult.title || 'Список треков' }}
              <b-badge variant="light-primary" class="ml-1">{{ importResult.tracks.length }}</b-badge>
              <b-button
                  v-if="importResult.url"
                  :href="importResult.url"
                  variant="warning"
                  class="btn-icon ml-1"
                  size="sm"
                  target="_blank"
              >
                <feather-icon icon="CastIcon"/>
              </b-button>
              <b-button
                  v-if="mode !== 'text'"
                  variant="primary"
                  class="btn-icon ml-1"
                  size="sm"
                  :disabled="isLoading"
                  title="Сбросить кеш и загрузить заново"
                  @click="refreshImport"
              >
                <feather-icon icon="RefreshCwIcon"/>
              </b-button>
            </h5>
          </b-card-header>
          <b-card-body>
            <!-- Описание плейлиста приходит из внешнего источника — выводим как текст, не как HTML -->
            <div
                v-if="importResult.description"
                class="blog-content mb-1"
                v-text="importResult.description"
            />
            <playlist-tracks-table
                :tracks="importResult.tracks"
                :is-loading="isLoading"
                @add-track="addTrack"
            />
          </b-card-body>
        </b-card>
      </b-row>
    </b-overlay>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardHeader,
  BCardBody,
  BFormInput,
  BFormTextarea,
  BFormRadioGroup,
  BButton,
  BBadge,
  BOverlay,
  BTable,
  BMedia,
  BAvatar,
  BPagination,
} from 'bootstrap-vue'
import Vue from 'vue'
import { computed, onUnmounted, ref, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { avatarText } from '@core/utils/filter'
import router from '@/router'
import store from '@/store'
import musicStoreModule from '@/views/db-music/musicStoreModule'
import PlaylistTracksTable from '@/views/db-music/PlaylistTracksTable.vue'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BFormInput,
    BFormTextarea,
    BFormRadioGroup,
    BButton,
    BBadge,
    BOverlay,
    BTable,
    BMedia,
    BAvatar,
    BPagination,
    PlaylistTracksTable,
  },
  setup() {
    const STORE_MODULE_NAME = 'playlist'
    const toast = useToast()

    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, musicStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const modeOptions = [
      { text: 'Яндекс', value: 'yandex' },
      { text: 'Spotify', value: 'spotify' },
      { text: 'Текст', value: 'text' },
      { text: 'ВКонтакте', value: 'vk' },
    ]

    const mode = ref('yandex')
    const playlistLink = ref('')
    const searchQuery = ref('')
    const tracklistText = ref('')

    const isLoading = ref(false)
    // Отдельный флаг именно для поиска: watch(page) срабатывает после сброса страницы
    // в searchPlaylists(true) и иначе отправил бы второй такой же запрос
    const isSearching = ref(false)
    const errorMessage = ref('')

    const importResult = ref(null)
    const lastImportPayload = ref(null)

    const playlistsList = ref([])
    const total = ref(0)
    const page = ref(1)

    const searchColumns = [
      { key: 'playlist', label: 'Плейлист', sortable: false },
      { key: 'owner_name', label: 'Автор', sortable: false },
      { key: 'tracks_count', label: 'Треков', sortable: false },
      { key: 'actions', label: 'Действия' },
    ]

    // Поиск плейлистов у Яндекса отдаёт по 10 результатов на страницу, у Spotify — по 20
    const perPage = computed(() => (mode.value === 'spotify' ? 20 : 10))
    const hasQuery = computed(() => ['yandex', 'spotify'].includes(mode.value))

    const getErrorText = error => error.response?.data?.message
      || error.response?.data?.error
      || error.message
      || 'Произошла ошибка'

    const resetResults = () => {
      importResult.value = null
      lastImportPayload.value = null
      playlistsList.value = []
      total.value = 0
      page.value = 1
      errorMessage.value = ''
    }

    const runImport = async payload => {
      isLoading.value = true
      errorMessage.value = ''

      try {
        const response = await store.dispatch('playlist/playlistImport', payload)
        importResult.value = response.data
        lastImportPayload.value = payload
      } catch (error) {
        importResult.value = null
        lastImportPayload.value = null
        errorMessage.value = getErrorText(error)
      } finally {
        isLoading.value = false
      }
    }

    const searchPlaylists = async reset => {
      if (isSearching.value) return

      isSearching.value = true
      isLoading.value = true
      errorMessage.value = ''
      importResult.value = null

      if (reset) {
        page.value = 1
      }

      const action = mode.value === 'yandex'
        ? 'playlist/yandexPlaylistSearch'
        : 'playlist/spotifyPlaylistSearch'

      try {
        const response = await store.dispatch(action, { q: searchQuery.value, page: page.value })
        playlistsList.value = response.data.playlists || []
        total.value = response.data.total || 0
      } catch (error) {
        playlistsList.value = []
        total.value = 0
        errorMessage.value = getErrorText(error)
      } finally {
        isLoading.value = false
        isSearching.value = false
      }
    }

    const submit = () => {
      if (mode.value === 'text') {
        playlistsList.value = []
        total.value = 0

        return runImport({ source: 'text', text: tracklistText.value })
      }

      if (playlistLink.value) {
        playlistsList.value = []
        total.value = 0

        return runImport({ source: mode.value, link: playlistLink.value })
      }

      if (hasQuery.value && searchQuery.value) {
        return searchPlaylists(true)
      }

      errorMessage.value = hasQuery.value ? 'Укажите ссылку или запрос' : 'Укажите ссылку'

      return null
    }

    const openPlaylist = item => runImport({
      source: mode.value,
      owner_id: item.owner_id,
      id: item.id,
    })

    const playlistViewUrl = item => {
      const route = mode.value === 'yandex'
        ? { name: 'yandex-playlist-view', params: { streaming: 'yandex', owner_id: item.owner_id, id: item.id } }
        : { name: 'spotify-playlist-view', params: { streaming: 'spotify', id: item.id } }

      return router.resolve(route).href
    }

    const refreshImport = () => {
      if (!lastImportPayload.value) return

      runImport({ ...lastImportPayload.value, refresh: 1 })
    }

    const addTrack = async track => {
      const firstArtist = track.artists && track.artists[0]

      isLoading.value = true
      errorMessage.value = ''

      try {
        let artistId = firstArtist && firstArtist.id ? firstArtist.id : null
        let isArtistCreated = false

        if (!artistId) {
          const artistResponse = await store.dispatch('playlist/createArtist', {
            title: firstArtist ? firstArtist.name : null,
          })
          artistId = artistResponse.data.data.id
          isArtistCreated = true
        }

        const addResponse = await store.dispatch('playlist/addTrack', {
          id: artistId,
          queryParams: { title: track.title },
        })

        const newTrackId = addResponse.data.track_id
          || (addResponse.data.data ? addResponse.data.data.id : addResponse.data.id)

        if (newTrackId) {
          // Точечно обновляем строку: повторный импорт всего плейлиста слишком дорог,
          // Spotify не кешируется и на большом плейлисте упирается в лимиты
          if (isArtistCreated && firstArtist) {
            Vue.set(firstArtist, 'id', artistId)
          }
          Vue.set(track, 'id', newTrackId)
        } else if (lastImportPayload.value) {
          // Сервер не вернул идентификатор трека — остаётся перезагрузить плейлист целиком
          const importResponse = await store.dispatch('playlist/playlistImport', lastImportPayload.value)
          importResult.value = importResponse.data
        }
      } catch (error) {
        errorMessage.value = getErrorText(error)
        toast({
          component: ToastificationContent,
          props: {
            title: 'Не удалось добавить трек',
            text: getErrorText(error),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    watch(mode, () => {
      resetResults()
    })

    watch(page, () => {
      if (playlistsList.value.length) searchPlaylists(false)
    })

    return {
      mode,
      modeOptions,
      playlistLink,
      searchQuery,
      tracklistText,
      isLoading,
      errorMessage,
      importResult,
      playlistsList,
      total,
      page,
      perPage,
      hasQuery,
      searchColumns,
      avatarText,
      submit,
      openPlaylist,
      playlistViewUrl,
      refreshImport,
      addTrack,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-blog.scss';
</style>
